import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Zap, Droplets, Trash2, Sun, Battery, Plug, PawPrint } from "lucide-react";
import EditableText from "@/components/editor/EditableText";

function InfoCard({ icon: Icon, title, contentKey, defaultText, delay = 0 }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="bg-white border border-wc-driftwood/20 rounded-md p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-wc-teal/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-wc-teal" />
        </div>
        <h2 className="font-display text-wc-forest text-xl font-semibold">{title}</h2>
      </div>
      <EditableText contentKey={contentKey} as="div" className="font-body text-wc-slate text-base leading-relaxed" defaultValue={defaultText} />
    </motion.div>
  );
}

export default function OffGridPage() {
  return (
    <div className="pt-16 min-h-screen bg-wc-cream">
      <div className="wc-section">
        <div className="max-w-4xl mx-auto">
          <p className="font-accent italic text-wc-teal text-base mb-3">Off Grid</p>
          <EditableText contentKey="offgrid_heading" as="h1"
            className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
            defaultValue="Living off the grid." />
          <EditableText contentKey="offgrid_subtitle" as="p"
            className="font-body text-wc-slate text-base sm:text-lg max-w-2xl leading-relaxed mb-12"
            defaultValue="Whale Cove is off-grid camping in Nova Scotia — proper boondocking on the Bay of Fundy, with the experience set up so you don't have to rough it. Here's what you need to know." />

          <div className="space-y-6">
            <InfoCard icon={Droplets} title="Water & Waste" contentKey="offgrid_water" delay={0.1}
              defaultText="There is no water hookup and no dump station on site. All RVs and campers must be fully self-contained with their own fresh water and waste holding tanks. Plan to arrive with full fresh water tanks — the nearest fill station is in Digby. Do not dump grey or black water anywhere on the property — all waste water must stay in your tanks and be disposed of at a proper facility when you leave. We recommend at least 4 litres of drinking water per person per day." />

            <InfoCard icon={Zap} title="Power" contentKey="offgrid_power" delay={0.2}
              defaultText="There is no power hookup on site. Bring a generator if you need electricity for your stay. Quiet hours for generators are 10 PM to 7 AM — please be respectful of the silence that makes this place special." />

            <InfoCard icon={PawPrint} title="Dogs & Pets" contentKey="offgrid_pets" delay={0.3}
              defaultText="Dogs are family, and they're welcome at Whale Cove — on-leash or off, we trust you to know your pup. A few heads-ups worth knowing before you arrive: the shoreline is rocky and uneven, the tides run 20+ feet twice a day and can move faster than you'd think, and Digby Neck is working land — there's fishing gear, the occasional piece of equipment, and neighbours with their own dogs and routines. Keep an eye on them near the water, pack out the waste, and please don't let them chase the wildlife. You're responsible for your animals while you're here. Beyond that — let them run." />
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 bg-wc-forest rounded-md p-6 sm:p-8">
            <h3 className="font-display text-[#F0EFEB] text-lg font-semibold mb-3">Leave No Trace</h3>
            <p className="font-body text-wc-driftwood text-sm leading-relaxed mb-4">Whale Cove is a wild, off-grid property and we intend to keep it that way. Please pack out everything you bring in — all garbage, recycling, food waste, and personal items. Leave your site exactly as you found it.</p>
            <ul className="space-y-2 font-body text-wc-driftwood text-sm leading-relaxed">
              <li className="flex gap-2"><Trash2 className="w-4 h-4 text-wc-teal mt-0.5 flex-shrink-0" />Pack out all garbage and recycling — there is no collection on site</li>
              <li className="flex gap-2"><Droplets className="w-4 h-4 text-wc-teal mt-0.5 flex-shrink-0" />No dumping grey water or black water on the property</li>
              <li className="flex gap-2"><Battery className="w-4 h-4 text-wc-teal mt-0.5 flex-shrink-0" />Bring a generator for power — charge devices before arrival</li>
              <li className="flex gap-2"><Plug className="w-4 h-4 text-wc-teal mt-0.5 flex-shrink-0" />Carry extra drinking water — at least 4L per person per day</li>
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
